import {faArrowDown, faArrowUp, faChevronRight, faSearch, faStepForward} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {observer} from 'mobx-react-lite';
import * as React from 'react';
import {FunctionComponent} from 'react';
import {Link} from 'react-router-dom';

import {integrationInstructionsLink, logOverviewLink} from '../../routing/AppRoutes';
import {formatDateTime} from '../../helpers/helpers';
import {FlatListOrder, LogType, Severity} from '../../swagger/models';
import {Button} from '../Button';
import {Card} from '../Card';
import {ErrorsChart} from '../ErrorsChart';
import {GraphTimeSpanComponent} from '../GraphTimeSpan';
import {TrendView} from '../TrendView';
import {FlatsListPaginatorComponent} from './FlatsListPaginatorComponent';
import {FlatsListSorterComponent} from './FlatsListSorter';
import {RangeDateTimePicker} from '../form/RangeDateTimePicker';
import {FlatListState} from './FlatListState';
import {DefaultGraphTimeSpan} from './FlatListForLogGenericState';
import {PauseButtonComponent} from '../PauseButton';
import {UptimeInfoComponent} from './UptimeInfoComponent';

export interface FlatsListProps {
    state: FlatListState;
}


export const FlatsListComponent: FunctionComponent<FlatsListProps> = observer(({state}) => {
    if (state.log !== undefined && state.log.logType == LogType.UptimeCheck) {
        return <Card
            id="flat-list"
            to={logOverviewLink(state.log.logId!)}
            title={state.log.name}
            listControls={<UptimeInfoComponent state={state}/>}
        >
            No entries because log type is uptime check only
        </Card>;
    }
    return (
        <React.Fragment>
            {state.log !== undefined && <Card
                id="flat-list"
                to={logOverviewLink(state.log.logId!)}
                title={state.log.name}
                cardControls={
                    state.showSearch && (
                        <React.Fragment>
                            <div className="md:flex-shrink-0 lg:flex">
                                <input
                                    className="form-control"
                                    type={'text'}
                                    placeholder={'search'}
                                    value={state.listParams.searchSearchValue || ''}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            state.search();
                                        }
                                    }}
                                    onChange={(e) => state.setSearchValue(e.target.value)}
                                />
                                <Button
                                    className="btn-primary"
                                    title="Search"
                                    onClick={() => state.search()}
                                >
                                    <FontAwesomeIcon className="fill-current" icon={faSearch}/>
                                </Button>
                                &nbsp;
                                <Button
                                    title="Advanced search"
                                    className="btn-primary"
                                    onClick={() => state.toggleAdvancedSearchHandler()}
                                >
                                    <FontAwesomeIcon className="fill-current" icon={state.advancedSearchExpanded ? faArrowUp : faArrowDown}/>
                                </Button>

                            </div>
                        </React.Fragment>
                    )
                }
                errorsChart={
                    state.log.graph && (
                        <ErrorsChart
                            logsIds={[state.log.logId!]}
                            containerPadding={21}
                            small={true}
                            items={state.log.graph}
                            graphTimeSpan={state.listParams.graphTimeSpan || DefaultGraphTimeSpan}
                        />
                    )
                }
                listControls={
                    <React.Fragment>
                        {state.advancedSearchExpanded && <form onSubmit={(e: any) => e.preventDefault()}>
                            <div className="flex items-center mb-4 mt-2">
                                <RangeDateTimePicker
                                    title="First seen: "
                                    fromValue={state.listParams.searchFirstSeenFrom}
                                    toValue={state.listParams.searchFirstSeenTo}
                                    onChangeDateFrom={(v) => state.setSearchFirstSeenFrom(v)}
                                    onChangeDateTo={(v) => state.setSearchFirstSeenTo(v)}/>
                            </div>
                            <div className="flex items-center mb-4 mt-2">
                                <RangeDateTimePicker
                                    title={'Last seen: '}
                                    fromValue={state.listParams.searchLastSeenFrom}
                                    toValue={state.listParams.searchLastSeenTo}
                                    onChangeDateFrom={(v) => state.setSearchLastSeenFrom(v)}
                                    onChangeDateTo={(v) => state.setSearchLastSeenTo(v)}/>
                            </div>
                            <div className="flex items-center mb-4 mt-2">Severity:
                                {Object.keys(Severity).map((s: any) => {
                                    return <Button
                                        key={'button' + s}
                                        className={'ml-2 btn-sm ' + (state.listParams.searchSeverities === undefined || state.listParams.searchSeverities?.includes(s) || state.listParams.searchSeverities?.length === 0 ? s : 'btn-secondary')}
                                        onClick={() => state.toggleSearchSeveritiy(s)}
                                    >{s}</Button>;
                                })}
                            </div>
                            <div className="flex mb-4 mt-4 mb-8">
                                <Button className="btn-primary  right-0" onClick={() => state.search()}>Search</Button>&nbsp;
                                <Button className="btn-secondary" onClick={() => {
                                    state.clearFilters();
                                    state.search();
                                }}>Clear filters</Button>&nbsp;
                                <Button className="btn-danger" onClick={() => state.delete()}>Delete entries</Button>
                            </div>

                        </form>}
                        <div className="flex items-center mb-4 mt-2">
                            <GraphTimeSpanComponent
                                currentGraphTimeSpan={state.listParams.graphTimeSpan || DefaultGraphTimeSpan}
                                changeGraphTimeSpanHandler={(v) => state.setGraphTimeSpan(v)}
                            />
                            <FlatsListSorterComponent
                                currentOrder={state.listParams.orderBy ?? FlatListOrder.LastSeen}
                                onChange={(v) => state.setOrderBy(v)}
                            />
                            <PauseButtonComponent state={state}/>
                        </div>
                        <UptimeInfoComponent state={state}/>
                    </React.Fragment>
                }
                footer={
                    <div className="flex flex-wrap items-center justify-between">
                        {state.log.totalPages! > 1 && state.showPagination && (
                            <FlatsListPaginatorComponent
                                state={state}
                            />
                        )}
                        {state.log?.total !== 0 && state.showFullListButton && (
                            <span className="ml-auto">
                                <Link className="flex items-center" to={logOverviewLink(state.log.logId!)}>
                                    Show full list
                                    <FontAwesomeIcon className="ml-4" icon={faChevronRight}/>
                                </Link>
                            </span>
                        )}
                    </div>
                }>
                <ul className="list-none">
                    {state.log?.items?.map((flat) => (
                        <li
                            key={'flatlistitem' + flat.flatId}
                            onClick={() => state.flatClickHandler(flat)}
                            className={'flat-list-item' + (state.currentFlatId === flat.flatId ? ' active ' : '')}
                        >
                            <span className={'flat-list-item-bullet ' + flat.severity}/>
                            <div className="ml-6 mr-2">
                                <div className="flat-list-title" title={flat.message || ''}>
                                    <span className="mr-4 truncate">{flat.message}</span>
                                    <span
                                        className="badge mr-2 right-0"
                                        style={{right: '-1rem'}}
                                        title={'Count: ' + flat.count}
                                    >
                                        {flat.count}
                                    </span>
                                    <TrendView trend={flat.trend}/>
                                </div>
                                <div className="flat-list-details">
                                    <div>
                                        <span title={'First time seen'}>{formatDateTime(flat.firstTimeSeen)}</span>
                                        &nbsp;<FontAwesomeIcon icon={faStepForward}/>&nbsp;
                                        <span title={'Last time seen'}>{formatDateTime(flat.lastTimeSeen)}</span>
                                    </div>
                                    <div className="flex flex-nowrap">
                                        {flat.version} <span className="truncate">{flat.fileName} &nbsp; </span>
                                        {/*{flat.lineNumber! > 0 && (
                                    <span className='whitespace-nowrap'>&nbsp;Line: {flat.lineNumber}</span>
                                )}*/}
                                    </div>
                                </div>
                            </div>
                        </li>
                    ))}
                    {(state.listParams.searchStructuralDataFilters?.length ?? 0) > 0 &&
                        <><Button className="btn-primary mt-4 mb-2" onClick={() => state.removeStructuralDataFilters()}>
                            Showing only errors related to structural data filters, click here to remove these filters
                        </Button><br/></>}
                    {state.log && state.log.total === 0 && (
                        <span>
                        No entries found in current list <br/>
                            <Link className="text-m-green" to={integrationInstructionsLink(state.log.logId!)}>
                            Show integration instructions
                            </Link>
                        </span>
                    )}


                </ul>
            </Card>}
        </React.Fragment>
    );
});
